import * as React from 'react';
import {
	createBrowserRouter,
	Navigate,
	RouterProvider,
	useParams,
} from 'react-router-dom';
import Editor from './pages/Editor';
import Gallery from './pages/Gallery';
import NotFoundPage from './pages/NotFoundPage';
import Auth from './pages/Auth';
import NewPaymentMethodCallback from './pages/NewPaymentMethodCallback';
import { Socket } from './socket/socket';
import { Events } from '@elyx-code/common-ts-utils';
import PricingPlans from './pages/PricingPlans';
import LegacyRouteRedirector from './components/LegacyRouteRedirector';
import WorkspaceLayout from './components/WorkspaceLayout';
import ErrorPage from './pages/ErrorPage';

export interface IRouterProps {
	socket: Socket;
	events: Events;
}

// Helper component for redirecting /ws/:workspaceId/editor/:id to /ws/:workspaceId/editor/:id/main/latest
const EditorIdRedirect: React.FC = () => {
	const { workspaceId, id } = useParams<{ workspaceId: string; id: string }>();
	return <Navigate to={`/ws/${workspaceId}/editor/${id}/main/latest`} replace />;
};

const EditorIdBranchRedirect: React.FC = () => {
	const { workspaceId, id, branch } = useParams<{ workspaceId: string; id: string; branch: string }>();
	return <Navigate to={`/ws/${workspaceId}/editor/${id}/${branch}/latest`} replace />;
};

const Router: React.FC<IRouterProps> = ({ socket, events }: IRouterProps) => {
	const router = createBrowserRouter([
		// --- Legacy / Root Intercepts ---
		{
			path: '/',
			element: <LegacyRouteRedirector />, // Will redirect to /ws/:id/gallery
			errorElement: <ErrorPage />,
		},
		{
			path: '/gallery/*',
			element: <LegacyRouteRedirector />,
			errorElement: <ErrorPage />,
		},
		{
			path: '/editor/:id/*',
			element: <LegacyRouteRedirector />,
			errorElement: <ErrorPage />,
		},

		// --- Global / Non-Workspace Routes ---
		{
			path: 'auth/:action',
			element: <Auth />,
			errorElement: <ErrorPage />,
		},
		{
			path: 'new-payment-method',
			element: <NewPaymentMethodCallback />,
			errorElement: <ErrorPage />,
		},
		{
			path: 'pricing/plans',
			element: <PricingPlans />,
			errorElement: <ErrorPage />,
		},

		// --- Workspace-Aware Routes ---
		{
			path: '/ws/:workspaceId',
			element: <WorkspaceLayout />,
			errorElement: <ErrorPage />,
			children: [
				{
					path: '',
					element: <Navigate to='gallery' replace />,
				},
				{
					path: 'gallery/*',
					element: <Gallery socket={socket} />,
				},
				{
					path: 'editor/:id',
					element: <EditorIdRedirect />,
				},
				{
					path: 'editor/:id/:branch',
					element: <EditorIdBranchRedirect />,
				},
				{
					path: 'editor/:id/:branch/:version/*',
					element: <Editor socket={socket} events={events} />,
				},
			],
		},

		// --- 404 ---
		{
			path: '*',
			element: <Navigate to='/404' replace />,
		},
		{
			path: '/404',
			element: <NotFoundPage />,
		},
	]);

	return <RouterProvider router={router} />;
};

export default Router;
